import {
    Component,
    EventEmitter,
    InputSignal,
    OnInit,
    Output,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';

import { CommonModule } from '@angular/common';
import {
    IqWorkflowActionProcessorService,
    createActionListNoRules,
} from '@dat/iq-workflow';
import { IDatiqAction, IDatiqRulesData } from '@dat/iq-proxy';
import { IqRateLaneUtilsStateService } from '../state';
import { IqErrorStateProv } from '../models';

@Component({
    selector: 'iq-rate-lane-utils-od-search-formatter',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-rate-lane-utils-od-search-formatter.component.html',
    styleUrl: './iq-rate-lane-utils-od-search-formatter.component.scss',
})
export class IqRateLaneUtilsODSearchFormatterComponent {
    id: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');
    origin: InputSignal<string> = input<string>('');
    destination: InputSignal<string> = input<string>('');
    equipmentType: InputSignal<string> = input<string>('');
    dashboardId: InputSignal<string> = input<string>('');
    sectionId: InputSignal<string> = input<string>('');
    componentId: InputSignal<string> = input<string>('');
    originControlId: InputSignal<string> = input<string>('');
    destinationControlId: InputSignal<string> = input<string>('');
    equipmentControlId: InputSignal<string> = input<string>('');
    originFetched: WritableSignal<string> = signal('');
    destinationFetched: WritableSignal<string> = signal('');
    @Output()
    loaded: EventEmitter<{ erroredGeo: boolean }> = new EventEmitter<{
        erroredGeo: boolean;
    }>();

    formattedOrigin = computed(async () => {
        const location = this.origin();
        const geo = await this._state.getGeo(location);
        return geo;
    });

    formattedDestination = computed(async () => {
        const location = this.destination();
        const geo = await this._state.getGeo(location);
        return geo;
    });

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    constructor(
        private readonly _actions: IqWorkflowActionProcessorService,
        private readonly _state: IqRateLaneUtilsStateService,
    ) {
        effect(async () => {
            const destination = await this.formattedDestination();
            const origin = await this.formattedOrigin();
            const equipmentType = this.equipmentType();
            if (!origin || !destination) {
                return;
            }
            untracked(async () => {
                this.updateODControls(
                    origin.displayName,
                    destination.displayName,
                    equipmentType,
                );
            });
        });
        effect(async () => {
            const [destination, origin] = await Promise.all([
                this.formattedDestination(),
                this.formattedOrigin(),
            ]);
            /**
             * This dispatches an event when the component clicks on a search
             *
             *
             * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
             *
             * ```json
             * {
             *  "onLoad": [
             *    {
             *      "name": "EVENT_NAME",
             *      "payload": {},
             *      "sourceEventRule": {
             *          "payload": [
             *            {
             *                "path": "id",
             *                "operator": "eq",
             *                "value": "COMPONENT_ID"
             *            },
             *            {
             *                "path": "componentName",
             *                "operator": "eq",
             *                "value": "IqTableRateRecentSearch"
             *            },
             *          ]
             *      },
             *      "rules": []
             *    },
             *   ],
             * }
             * ```
             */
            const dispatch = () =>
                this._eventBus.dispatch({
                    name: 'type:load',
                    payload: {
                        id: this.id(),
                        componentName: 'IqRateLaneUtilsODSearchFormatter',
                        erroredGeo: this.isErroringStateProv(
                            origin.stateProv,
                            destination.stateProv,
                        ),
                    },
                });

            dispatch();
        });
    }

    private updateODControls(
        origin: string,
        destination: string,
        equipmentType: string,
    ) {
        if (!this.controlIdsValid()) {
            return;
        }

        const actions: IDatiqRulesData<IDatiqAction>[] =
            createActionListNoRules([
                {
                    name: 'updateControlProps',
                    payload: {
                        dashboardId: this.dashboardId(),
                        sectionId: this.sectionId(),
                        componentId: this.componentId(),
                        controlId: this.originControlId(),
                        props: {
                            value: {
                                displayName: `<%- origin %>`,
                            },
                        },
                    },
                },
                {
                    name: 'updateControlProps',
                    payload: {
                        dashboardId: this.dashboardId(),
                        sectionId: this.sectionId(),
                        componentId: this.componentId(),
                        controlId: this.destinationControlId(),
                        props: {
                            value: {
                                displayName: `<%- destination %>`,
                            },
                        },
                    },
                },
                {
                    name: 'updateControlProps',
                    payload: {
                        dashboardId: this.dashboardId(),
                        sectionId: this.sectionId(),
                        componentId: this.componentId(),
                        controlId: this.equipmentControlId(),
                        props: {
                            value: {
                                value: `<%- equipmentType %>`,
                            },
                        },
                    },
                },
            ]);
        this._actions.processActions(actions, {
            origin,
            destination,
            equipmentType,
        });
    }
    private controlIdsValid() {
        return (
            this.originControlId() &&
            this.destinationControlId() &&
            this.equipmentControlId() &&
            this.dashboardId() &&
            this.sectionId() &&
            this.componentId()
        );
    }

    isErroringStateProv(origin: string, destination: string): boolean {
        return (
            (Object.values(IqErrorStateProv) as string[]).includes(
                origin?.trim(),
            ) ||
            (Object.values(IqErrorStateProv) as string[]).includes(
                destination?.trim(),
            )
        );
    }
}
